(ns dnd.template.management
  (:require [dnd.constants :refer [session-uuid]]
            [dnd.events.core :refer [standard-interceptors
                                     fx-interceptors
                                     reg-event-ds]]
            [dnd.util :refer [>evt
                              >evt-sync
                              cls
                              make-uuid
                              iso-string->display-shortdate]]
            [datascript.core :as d]
            [material-ui.core :as m]
            [re-frame.core :as rf]
            [dnd.gateway.app-db :as fdb]
            [austinbirch.reactive-entity :as re]
            [com.wsscode.async.async-cljs :refer [go
                                                  go-catch
                                                  <?
                                                  <!p]]
            [promesa.core :as p]
            ["luxon" :refer [DateTime]]
            [dnd.analytics]
            [dnd.template.editor]
            [och-graph.core :as gg]
            [dnd.placeholder-suggestions]
            [clojure.string :as str]
            [reagent.core :as r]
            [dnd.ui.orgchart.preview]
            [dnd.ui.motion :as ui.motion]
            [dnd.domain.model :as domain.model]
            [dnd.template.ui :as template.ui]
            [dnd.ui.components.theme :refer [trellis-enabled?]]
            [dnd.ui.components.core :as components.core]
            [dnd.undo-redo :as undo-redo]))

(def blueprint-reveal-animation-durations
  {:blueprint-reveal-animation.step/loading (* 1 1000)
   :blueprint-reveal-animation.step/sliding-in (* 1.5 1000)
   :blueprint-reveal-animation.step/showing-blueprint (* 1 1000)
   :blueprint-reveal-animation.step/fading-blueprint (* 1 1000)
   :blueprint-reveal-animation.step/done (* 0.1 1000)})

(defn make-blueprint-reveal-animation
  []
  {:id (make-uuid)
   :blueprint-reveal-animation/step :blueprint-reveal-animation.step/loading
   :blueprint-reveal-animation/step-duration (get blueprint-reveal-animation-durations
                                                  :blueprint-reveal-animation.step/loading)})

(defn advance-blueprint-reveal-animation
  [animation-state]
  (let [current-step (:blueprint-reveal-animation/step animation-state)
        pair-for-step (fn [step]
                        (let [duration (get blueprint-reveal-animation-durations step)]
                          [step duration]))
        [next-step duration] (condp = current-step
                               :blueprint-reveal-animation.step/loading
                               (pair-for-step :blueprint-reveal-animation.step/sliding-in)

                               :blueprint-reveal-animation.step/sliding-in
                               (pair-for-step :blueprint-reveal-animation.step/showing-blueprint)

                               :blueprint-reveal-animation.step/showing-blueprint
                               (pair-for-step :blueprint-reveal-animation.step/fading-blueprint)

                               :blueprint-reveal-animation.step/fading-blueprint
                               (pair-for-step :blueprint-reveal-animation.step/done))]
    {:next-animation-state (assoc animation-state
                             :blueprint-reveal-animation/step next-step
                             :blueprint-reveal-animation/step-duration duration)
     :duration duration}))

(rf/reg-event-fx
  :events.template-management/create-new-template
  fx-interceptors
  (fn [{:keys [ds]} [_ {:keys [via-chart-onboarding?]
                        :or {via-chart-onboarding? false}}]]
    (let [session (d/entity ds [:uuid session-uuid])
          portal-id (:session/portal-id session)]
      (go
        (try
          (let [template (<!p (fdb/create-template-doc {:token (:session/token session)
                                                        :portal-id portal-id}))]
            (>evt [:events.template-management.create-new-template/success
                   {:template template
                    :return-to-template-management? (not via-chart-onboarding?)}]))
          (catch :default e
            (js/console.error e))))
      {:fx [[:transact [{:uuid session-uuid
                         :session.template-editor/creating-template? true}]]]})))

(rf/reg-event-fx
  :events.template-management.create-new-template/success
  fx-interceptors
  (fn [_ [_ {:keys [template
                    return-to-template-management?]}]]
    {:fx [[:transact [template
                      {:uuid session-uuid
                       :session.template-editor/creating-template? false}]]
          [:dispatch [:events.template-management.edit-template/start
                      {:template-id (:template/id template)
                       :focus-root-node? true
                       :return-to-template-management? return-to-template-management?}]]]}))

(rf/reg-event-fx
  :events.template-management/open
  fx-interceptors
  (fn []
    {:transact [[:db/add [:uuid session-uuid] :session.template-management/is-open? true]]}))

(rf/reg-event-fx
  :events.template-management/close
  fx-interceptors
  (fn []
    {:transact [[:db/add [:uuid session-uuid] :session.template-management/is-open? false]]}))

(rf/reg-event-fx
  :events.template-management/save
  fx-interceptors
  (fn [{:keys [ds]}]
    (let [session (d/entity ds [:uuid session-uuid])
          template-id (:session.template-editor/active-template-id session)
          template (d/entity ds [:template/id template-id])
          portal-id (:session/portal-id session)
          next-graph (:template.local/graph2 template)]
      (go
        (try
          (<!p (fdb/update-template-doc {:token (:session/token session)
                                         :portal-id portal-id
                                         :template-id template-id}
                                        {:template/graph2 next-graph}))
          (catch :default e
            (js/console.error e)))))
    {:fx [[:dispatch [:events.template-editor/exit]]]}))

(rf/reg-event-fx
  :events.template-management/update-name
  fx-interceptors
  (fn [{:keys [ds]} [_ {:keys [template-name]}]]
    (let [session (d/entity ds [:uuid session-uuid])
          template-id (:session.template-editor/active-template-id session)
          portal-id (:session/portal-id session)]
      (go
        (try
          (<!p (fdb/update-template-doc {:token (:session/token session)
                                         :portal-id portal-id
                                         :template-id template-id}
                                        {:template/name template-name}))
          (catch :default e
            (js/console.error e))))
      ;; optimistically update locally, will be reset on success/error by realtime listeners
      {:fx [[:transact [{:template/id template-id
                         :template/name template-name}]]]})))

(rf/reg-event-fx
  :events.template-management/archive
  fx-interceptors
  (fn [{:keys [ds]}]
    (let [session (d/entity ds [:uuid session-uuid])
          template-id (:session.template-editor/active-template-id session)
          portal-id (:session/portal-id session)]
      (go
        (try
          (<!p (fdb/update-template-doc {:token (:session/token session)
                                         :portal-id portal-id
                                         :template-id template-id}
                                        {:template/archived true}))
          (>evt [:events.template-management.archive/success])
          (catch :default e
            (js/console.error e))))
      {:fx [[:transact [{:uuid session-uuid
                         :session.template-editor/archiving? true}
                        ;; optimistically update locally, will be reset on success/error by realtime listeners
                        {:template/id template-id
                         :template/archived true}]]]})))

(rf/reg-event-fx
  :events.template-management.archive/success
  fx-interceptors
  (fn []
    {:fx [[:transact [[:db.fn/retractAttribute [:uuid session-uuid] :session.template-editor/archiving?]]]]}))

(rf/reg-event-fx
  :events.template-management/unarchive
  fx-interceptors
  (fn [{:keys [ds]}]
    (let [session (d/entity ds [:uuid session-uuid])
          template-id (:session.template-editor/active-template-id session)
          portal-id (:session/portal-id session)]
      (go
        (try
          (<!p (fdb/update-template-doc {:token (:session/token session)
                                         :portal-id portal-id
                                         :template-id template-id}
                                        {:template/archived false}))
          (>evt [:events.template-management.unarchive/success])
          (catch :default e
            (js/console.error e))))
      {:fx [[:transact [{:uuid session-uuid
                         :session.template-editor/archiving? true}
                        ;; optimistically update locally, will be reset on success/error by realtime listeners
                        {:template/id template-id
                         :template/archived false}]]]})))

(rf/reg-event-fx
  :events.template-management.unarchive/success
  fx-interceptors
  (fn []
    {:fx [[:transact [[:db.fn/retractAttribute [:uuid session-uuid] :session.template-editor/archiving?]]]]}))

(rf/reg-event-fx
  :events.template-management.edit-template/start
  fx-interceptors
  (fn [{:keys [ds]} [_ {:keys [template-id
                               focus-root-node?
                               return-to-template-management?]
                        :or {focus-root-node? false
                             return-to-template-management? true}}]]
    (let [template (d/entity ds [:template/id template-id])
          root-node-id (when focus-root-node?
                         (let [template-graph (:template/graph2 template)]
                           (first (gg/root-node-ids template-graph))))]
      {:fx [[:transact [(cond-> {:uuid session-uuid
                                 :session.template-management/is-open? true
                                 :session.template-editor/active-template-id template-id
                                 :session.template-editor/return-to-template-management? return-to-template-management?}
                                root-node-id
                                (assoc :session.template.local/selected-node-id root-node-id))
                        {:template/id template-id
                         :template.local/graph2 (:template/graph2 template)}]]]})))

(rf/reg-event-fx
  :events.template-management.generate-chart/start
  fx-interceptors
  (fn [{:keys [ds]} [_ {:keys [template-id]}]]
    (let [session (d/entity ds [:uuid session-uuid])
          portal-id (:session/portal-id session)
          chart (domain.model/get-current-chart ds)
          chart-id (:chart.local/id chart)
          graph (:chart/graph2 chart)
          undo-state (or (:chart.local/undo-state chart) {})
          next-undo-state (or (undo-redo/record undo-state graph "generate-chart/start") undo-state)
          template (d/entity ds [:template/id template-id])
          next-graph (:template/graph2 template)
          template-job-titles (gg/get-placeholder-job-titles-on-chart next-graph)
          update-chart-or-reset-and-throw! (fn update-chart-or-reset-and-throw! []
                                             (go-catch
                                               (try
                                                 (<? (fdb/update-chart-update-on-graph2-change
                                                       {:token (:session/token session)
                                                        :portal-id portal-id
                                                        :chart.local/id chart-id}
                                                       {:chart/graph2 next-graph
                                                        :chart/updated-at (.toISO (.utc DateTime))
                                                        :chart/created-at (:chart/created-at chart)
                                                        :chart/vid-index {}
                                                        :chart/placeholder-suggestions {:suggestions []}
                                                        :chart/created-from-template-id template-id}))
                                                 (catch :default e
                                                   ;; if the update fails we need to force our local state to be the old state
                                                   (let [db-chart (<!p (fdb/get-chart-doc {:token (:session/token session)
                                                                                           :portal-id portal-id
                                                                                           :chart.local/id chart-id}))]
                                                     (rf/dispatch [:chart.fetch/reset-to-server-version db-chart]))
                                                   ;; then re-throw the error so that we skip any remaining work
                                                   ;; as we did not successfully update the chart in the db
                                                   (throw e)))))]
      (go-catch
        (try
          ;; update the chart in the database with the template
          ;; placeholder nodes, and automatically reset if
          ;; something goes wrong
          (<? (update-chart-or-reset-and-throw!))

          (<!p (fdb/trigger-suggested-contacts+
                 {:token (:session/token session)
                  :chart.local/id chart-id
                  :company-id (:session/company-id session)
                  :job-titles template-job-titles}))

          (catch :default e
            (js/console.error e))))
      {:fx [[:transact [{:uuid session-uuid
                         :session/template-generation-def {:template-id template-id}}
                        {:chart.local/id chart-id
                         :chart.local/undo-state next-undo-state}]]
            [:update-onboarding {:onboarding/event :onboarding.event/on-start-from-template}]
            [:dispatch [:events.template-management.blueprint-animation/start]]
            [:analytics/log-event {:event (dnd.analytics/make-event-chart-created-from-template
                                           {:template-id template-id
                                            :template-name (:template/name template)})}]]})))

(rf/reg-event-fx
  :events.template-management.generate-chart/apply-suggestions
  fx-interceptors
  (fn [{:keys [ds]}]
    (let [session (d/entity ds [:uuid session-uuid])
          chart (domain.model/get-current-chart ds)
          placeholder-suggestions (:chart/placeholder-suggestions chart)
          {next-graph :graph2
           next-accepts :accepts} (dnd.placeholder-suggestions/automatically-apply-suggestions
                                    {:graph2 (:chart/graph2 chart)
                                     :placeholder-suggestions placeholder-suggestions})
          next-suggestions (assoc placeholder-suggestions
                             :accepts next-accepts)]
      (-> (fdb/update-chart-doc
            {:token (:session/token session)
             :portal-id (:session/portal-id session)
             :chart.local/id (:chart.local/id chart)}
            {:placeholder-suggestions next-suggestions})
          (p/catch js/console.error))
      {:fx [[:transact [[:db.fn/retractAttribute [:uuid session-uuid] :session/template-generation-def]]]
            [:dispatch [:events.graph/change3 {:graph next-graph :description "apply-template-suggestions" :undoable? false}]]]})))

(reg-event-ds
  :events.template-management/fetched-template
  standard-interceptors
  (fn [_ [_ template]]
    [template]))

(rf/reg-event-fx
  :events.template-management.blueprint-animation/start
  fx-interceptors
  (fn []
    (let [animation-state (make-blueprint-reveal-animation)]
      {:fx [[:transact [{:uuid session-uuid
                         :session.animations/blueprint-reveal animation-state}]]
            [:dispatch-later {:ms 1000
                              :dispatch [:events.template-management.blueprint-animation/advance]}]]})))

(rf/reg-event-fx
  :events.template-management.blueprint-animation/advance
  fx-interceptors
  (fn [{:keys [ds]}]
    (let [session (d/entity ds [:uuid session-uuid])
          animation-state (:session.animations/blueprint-reveal session)]
      (if (= (:blueprint-reveal-animation/step animation-state)
             :blueprint-reveal-animation.step/done)
        {:fx [[:transact [[:db.fn/retractAttribute [:uuid session-uuid] :session.animations/blueprint-reveal]]]]}
        (let [{:keys [duration
                      next-animation-state]} (advance-blueprint-reveal-animation animation-state)]
          {:fx [[:transact [{:uuid session-uuid
                             :session.animations/blueprint-reveal next-animation-state}]]
                [:dispatch-later {:ms duration
                                  :dispatch [:events.template-management.blueprint-animation/advance]}]]})))))

(defn ui-template-management-selector-row
  [{:keys [<template
           on-click]}]
  (let [preview-height 68]
    [:button {:type "button"
              :class (cls "flex-none"
                          "group"
                          "text-left"
                          "transition-all cursor-pointer"
                          "rounded-md overflow-hidden"
                          (if (trellis-enabled?)
                            (cls "bg-trellis-color-fill-surface-default"
                                 "border-trellis-border-width-100"
                                 "border-trellis-color-border-core-default"
                                 "hover:shadow-md"
                                 "active:bg-trellis-color-fill-surface-recessed")
                            (cls "shadow hover:shadow-md active:shadow-inner"
                                 "bg-white active:bg-slate-200")))
              :onClick on-click}
     [:div {:class (cls "flex flex-row items-center"
                        "p-3"
                        "space-x-4")}
      [:div {:style {:height preview-height
                     :width (* preview-height 1.5)}
             :class (cls "rounded overflow-hidden"
                         "flex-none")}
       [dnd.ui.orgchart.preview/template-preview
        {:<template <template
         :preview-color (if (:template/archived <template)
                          "slate"
                          "blueprint")}]]
      [:div {:class (cls "flex-auto"
                         "flex flex-row"
                         "items-center"
                         "space-x-6")}
       [:div {:class (cls "flex flex-col"
                          "flex-auto")}
        [:span {:class (if (trellis-enabled?)
                         (cls "text-trellis-typography-heading-100-font-family"
                              "text-trellis-typography-heading-100-font-size"
                              "font-trellis-typography-heading-100-font-weight"
                              "text-trellis-color-text-primary"
                              "truncate")
                         (cls "text-base font-semibold text-slate-900 leading-normal truncate"))}
         (:template/name <template)]
        [:span {:class (if (trellis-enabled?)
                         (cls "text-trellis-typography-detail-200-font-family"
                              "text-trellis-typography-detail-200-font-size"
                              "text-trellis-typography-detail-200-font-weight"
                              "text-trellis-color-text-secondary"
                              "truncate")
                         (cls "text-xs font-regular text-slate-800"
                              "truncate"))}
         (let [job-titles (->> (:template/graph2 <template)
                               (domain.model/placeholder-job-titles-on-graph)
                               (str/join ", "))]
           job-titles)]]
       [:div {:class (cls "flex-none"
                          "flex flex-row"
                          "items-center"
                          "transition-all"
                          "pl-3 pr-2 py-2"
                          "rounded"
                          (if (trellis-enabled?)
                            (cls "text-trellis-typography-body-100-font-family"
                                 "text-trellis-typography-body-100-font-size"
                                 "font-trellis-typography-body-100-font-weight"
                                 "text-trellis-color-text-primary"
                                 "hover:bg-trellis-color-fill-surface-recessed"
                                 "group-active:bg-transparent")
                            (cls "text-sm leading-none font-medium text-slate-800"
                                 "hover:bg-slate-100 group-active:bg-transparent")))}
        [:span "View"]
        [:span {:class (cls "text-[20px]")}
         [:> m/ChevronRightIcon {:fontSize "inherit"
                                 :color "inherit"}]]]]]]))

(defn ui-template-management-selector
  [{:keys [on-close]}]
  (let [state (r/atom {:edit-archived? false})
        handle-create-template (fn []
                                 (>evt-sync [:events.template-management/create-new-template]))]
    (fn []
      (let [<session (re/entity [:uuid session-uuid])
            <templates (re/entities :template/id)
            <archived-templates (->> <templates
                                     (filter :template/archived))
            <live-templates (->> <templates
                                 (filter (fn [<template]
                                           (not (:template/archived <template)))))
            showing-archived? (true? (:edit-archived? @state))
            creating-template? (boolean (:session.template-editor/creating-template? <session))]
        [:div {:class (cls "flex flex-col"
                           "overflow-hidden"
                           (if (trellis-enabled?)
                             nil
                             "bg-slate-100")
                           "select-none"
                           "max-h-[550px]")}
         ;; header
         [:div {:class (cls "flex flex-row"
                            "flex-none"
                            "pl-6 pr-2 py-2"
                            "items-center justify-between"
                            (if (trellis-enabled?)
                              (cls "bg-trellis-color-fill-surface-default"
                                   "border-b-trellis-border-width-100"
                                   "border-trellis-color-border-core-subtle")
                              (cls "bg-slate-50 border-b border-slate-200")))}
          [:span {:class (if (trellis-enabled?)
                           (cls "text-trellis-typography-heading-200-font-family"
                                "text-trellis-typography-heading-200-font-size"
                                "font-trellis-typography-heading-200-font-weight"
                                "text-trellis-color-text-primary")
                           (cls "text-lg font-semibold text-slate-800"))}
           (if showing-archived?
             "Archived Templates"
             "Manage Templates")]
          [:> m/IconButton {:onClick on-close}
           [:> m/CloseIcon]]]

         [:div {:class (cls "flex-auto"
                            "flex flex-col")}
          (cond
            ;; empty state UI
            (and (not showing-archived?)
                 (empty? <live-templates))
            [template.ui/ui-no-blueprints-empty-state
             {:on-create handle-create-template
              :creating-template? creating-template?}]

            ;; template list
            :else
            [:div {:class (cls "flex flex-col"
                               "flex-auto"
                               "overflow-y-scroll overflow-x-visible"
                               "space-y-4"
                               "px-4 -ml-4 pl-8 py-4")}
             (->> (if showing-archived?
                    <archived-templates
                    <live-templates)
                  (map (fn [<template]
                         [ui-template-management-selector-row
                          {:key (:template/id <template)
                           :<template <template
                           :on-click (fn [e]
                                       (.stopPropagation e)
                                       (>evt [:events.template-management.edit-template/start
                                              {:template-id (:template/id <template)}]))}]))
                  (doall))])]

         ;; footer
         (when (or (seq <archived-templates)
                   (seq <live-templates))
           [:div {:class (cls "flex-none"
                              "p-4"
                              "flex flex-row"
                              "justify-between"
                              (if (trellis-enabled?)
                                (cls "bg-trellis-color-fill-surface-default")
                                (cls "bg-slate-50 border-t border-slate-200")))}
            (if (or (not (trellis-enabled?)) (seq <archived-templates))
              [:div {:class (cls "flex flex-row"
                                 "justify-end")}
               (when (seq <archived-templates)
                 (if (trellis-enabled?)
                   [components.core/button
                    {:variant "tertiary"
                     :size "md"
                     :onClick (fn []
                                (swap! state update :edit-archived? not))}
                    (if (:edit-archived? @state)
                      "Hide Archived Templates"
                      "Show Archived Templates")]
                   [:> m/Button
                    {:variant "text"
                     :style {:text-transform "none"}
                     :color "default"
                     :onClick (fn []
                                (swap! state update :edit-archived? not))}
                    (if (:edit-archived? @state)
                      "Hide Archived Templates"
                      "Show Archived Templates")]))]
              nil)
            (when (seq <live-templates)
              [ui.motion/animate-presence {:initial false}
               (when-not (:edit-archived? @state)
                 [ui.motion/div {:initial {:opacity 0}
                                 :animate {:opacity 1}
                                 :exit {:opacity 0}}
                  (if (trellis-enabled?)
                    [:div {:class (cls "flex flex-row gap-2")}
                     [components.core/button
                      {:variant "primary"
                       :size "md"
                       :disabled creating-template?
                       :onClick handle-create-template}
                      (if creating-template?
                        [:span {:class (cls "flex items-center justify-center")}
                         [:> m/CircularProgress
                          {:size 18}]]
                        "Create Template")]
                     [components.core/button
                      {:variant "secondary"
                       :size "md"
                       :onClick on-close}
                      "Cancel"]]
                    [:> m/Button
                     {:variant "contained"
                      :disabled creating-template?
                      :disableElevation true
                      :style {:text-transform "none"
                              :height "100%"}
                      :color "primary"
                      :onClick handle-create-template}
                     (if creating-template?
                       [:span {:class (cls "flex items-center justify-center")}
                        [:> m/CircularProgress
                         {:size 18}]]
                       "Create Template")])])])])]))))

(defn ui-template-management-dialog
  []
  (let [<session (re/entity [:uuid session-uuid])
        template-editor-visible? (boolean (:session.template-editor/active-template-id <session))
        on-close (fn []
                   (when-not template-editor-visible?
                     (>evt [:events.template-management/close])))]
    [:> m/Dialog
     {:open true
      :fullWidth true
      :disableBackdropClick template-editor-visible?
      :maxWidth (if template-editor-visible?
                  "xl"
                  "sm")
      :onClose on-close}
     [:div {:data-hubspot-theme (if (trellis-enabled?)
                                  "trellis-light"
                                  "och-legacy")
            :class (cls "flex flex-col flex-auto"
                        (if (trellis-enabled?)
                          "lexend-deca"
                          "inter"))}
      (if template-editor-visible?
        [dnd.template.editor/ui-template-editor]
        [ui-template-management-selector {:on-close on-close}])]]))

(comment

  (>evt [:events.template-management/archive
         {:template-id "XDCCu6pjuxK7d8vUvOIC"}])

  (>evt [:events.template-management/generate-chart
         {:template-id "XDCCu6pjuxK7d8vUvOIC"
          :company-id "9122450565"}])

  (>evt [:events.och.init/subscribe-templates])

  (-> (d/entity @dnd.db/conn [:template/id "XDCCu6pjuxK7d8vUvOIC"])
      (d/touch)
      js/console.log)

  (>evt [:events.template-management/open])

  (>evt [:events.template-management/close])

  )
